import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { Card, CardHeader, Button, Table } from '@/components/UI';
import type { OrganizationMember } from '@/types';

export function TeamMembers() {
  const { currentOrganization } = useAuthStore();
  const {
    members,
    userRole,
    fetchMembers,
    updateMemberRole,
    removeMember,
  } = useOrganizationStore();

  useEffect(() => {
    if (currentOrganization) {
      fetchMembers(currentOrganization.id);
    }
  }, [currentOrganization]);

  const canManageTeam = userRole === 'owner' || userRole === 'admin';
  const hasPaidPlan = currentOrganization?.plan !== 'free';

  const columns = [
    {
      key: 'user_id' as keyof OrganizationMember,
      header: 'Member',
      render: (member: OrganizationMember) => (
        <div>
          <p className="font-medium text-gray-900">User ID: {member.user_id.slice(0, 8)}...</p>
          <p className="text-sm text-gray-500">Member since {new Date(member.created_at).toLocaleDateString()}</p>
        </div>
      ),
    },
    {
      key: 'role' as keyof OrganizationMember,
      header: 'Role',
      render: (member: OrganizationMember) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          member.role === 'owner'
            ? 'bg-purple-100 text-purple-800'
            : member.role === 'admin'
            ? 'bg-blue-100 text-blue-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {member.role}
        </span>
      ),
    },
    {
      key: 'actions' as string,
      header: '',
      className: 'text-right',
      render: (member: OrganizationMember) => {
        if (!canManageTeam || member.role === 'owner') return null;
        return (
          <div className="flex justify-end gap-2">
            <select
              value={member.role}
              onChange={(e) => updateMemberRole(currentOrganization!.id, member.user_id, e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeMember(currentOrganization!.id, member.user_id)}
            >
              Remove
            </Button>
          </div>
        );
      },
    },
  ];

  if (!hasPaidPlan) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-600">Manage your organization members</p>
        </div>

        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Team collaboration</h3>
            <p className="mt-1 text-gray-500 max-w-md mx-auto">
              Team collaboration is available on Starter, Growth, and Pro plans.
              Upgrade to invite team members to your organization.
            </p>
            <div className="mt-6">
              <Button onClick={() => window.location.href = '/billing'}>
                Upgrade Plan
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-600">Manage your organization members</p>
        </div>
        {canManageTeam && (
          <Button>Invite Member</Button>
        )}
      </div>

      <Card padding="none">
        <Table
          columns={columns}
          data={members}
          keyExtractor={(m) => m.id}
          emptyMessage="No team members yet"
        />
      </Card>

      <Card>
        <CardHeader title="Roles & Permissions" />
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 mt-0.5">
              owner
            </span>
            <div>
              <p className="font-medium text-gray-900">Owner</p>
              <p className="text-sm text-gray-500">
                Full access to all features including billing, team management, and settings.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 mt-0.5">
              admin
            </span>
            <div>
              <p className="font-medium text-gray-900">Admin</p>
              <p className="text-sm text-gray-500">
                Can manage sites, view analytics, and invite team members. Cannot access billing.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 mt-0.5">
              member
            </span>
            <div>
              <p className="font-medium text-gray-900">Member</p>
              <p className="text-sm text-gray-500">
                Can view sites and analytics. Cannot manage settings or invite members.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default TeamMembers;
